import React, { Component } from 'react';
import { string } from 'prop-types';
import { range } from 'lodash-es';
import { Normal } from 'distributions';
import {
  variableEditorType,
  variableTypeValues,
  relativeCategoryTypeValues
} from '../../../../constants/types';
import VariableSummary from '../../../VariableSummary';
import {
  getTranslation, getTranslationWithLocale, withLang, langType
} from '../../../../i18n';

import styles from './variableFeedbackPreview.module.css';

const relativeCategoryNameTranslationKey = (type) => {
  const translationNamespace = 'surveySchemaParts.dimensions.relativeFeedback';
  switch (type) {
    case relativeCategoryTypeValues[0]:
      return `${translationNamespace}.belowAverage`;
    case relativeCategoryTypeValues[1]:
      return `${translationNamespace}.average`;
    case relativeCategoryTypeValues[2]:
      return `${translationNamespace}.overAverage`;
    default:
      return '';
  }
};

const compareRelativeCategoriesByType = (c1, c2) => (
  relativeCategoryTypeValues.indexOf(c1.relativeCategory.type)
  < relativeCategoryTypeValues.indexOf(c2.relativeCategory.type)
    ? -1 : 1
);

const getMinVariableValue = (questions) => {
  const minQuestionValue = Math.min(...questions[0].responseOptions.map((v) => v.value));
  return minQuestionValue * questions.length;
};

const getMaxVariableValue = (questions) => {
  const maxQuestionValue = Math.max(...questions[0].responseOptions.map((v) => v.value));
  return maxQuestionValue * questions.length;
};

const getExampleAverageVariableValue = (questions) =>
  (questions.length ? getMaxVariableValue(questions) / 2 : 0);

const getAverage = (values) => values.reduce((acc, x) => acc + x, 0) / values.length;

const getStandardDeviation = (values, mean) => {
  const diffs = values.map((value) => value - mean);
  const squareDiffs = diffs.map((diff) => diff * diff);
  const avgSquareDiff = getAverage(squareDiffs);
  return Math.sqrt(avgSquareDiff);
};

const getExampleDistributionForRelativeVariable = (questions, valueRange) => {
  const mean = getAverage(valueRange);
  const stdDev = getStandardDeviation(valueRange, mean);
  const normalDistribution = Normal(mean, stdDev);

  return valueRange.reduce((acc, x) => {
    acc[x] = normalDistribution.pdf(x);
    return acc;
  }, {});
};

const getUserAverageForRelativeVariable = (groupAverage, categoryIndex) => {
  switch (categoryIndex) {
    case 0:
      return groupAverage - 1;
    case 1:
      return groupAverage;
    case 2:
      return groupAverage + 1;
    default:
      return groupAverage;
  }
};

const isRelativeVariable = (responseCategories) => responseCategories[0].relativeCategory;

const getVariableType = (responseCategories) =>
  (isRelativeVariable(responseCategories)
    ? variableTypeValues.RELATIVE
    : variableTypeValues.ABSOLUTE);

const getAbsoluteVariablePreviewValues = (responseCategories, categoryIndex) => ({
  absoluteCategories: responseCategories.map((v) => v.absoluteCategory),
  userTotal: responseCategories[categoryIndex].absoluteCategory.value
});

const getRelativeVariablePreviewValues = (questions, responseOptionValues, categoryIndex) => {
  const getMiddleValue = (values) => values[Math.round((values.length - 1) / 2)];
  const average = getMiddleValue(responseOptionValues);

  return {
    average,
    distribution: getExampleDistributionForRelativeVariable(questions, responseOptionValues),
    userAverage: getUserAverageForRelativeVariable(average, categoryIndex)
  };
};

const getVariablePreviewValues = (variable, categoryIndex) => {
  const { responseCategories, questions } = variable;

  const minTotal = getMinVariableValue(questions);
  const maxTotal = getMaxVariableValue(questions);
  const scaleStartValue = Math.min(...questions[0].responseOptions.map((v) => v.value));
  const scaleEndValue = Math.max(...questions[0].responseOptions.map((v) => v.value));
  const responseOptionValues = range(scaleStartValue, scaleEndValue + 1);

  const commonPreviewValues = {
    averageTotal: getExampleAverageVariableValue(questions),
    minTotal,
    maxTotal,
    average: 0,
    distribution: {},
    type: getVariableType(responseCategories),
    responseOptionValues,
    feedback: responseCategories[categoryIndex].feedback,
    improvementSuggestions: responseCategories[categoryIndex].improvementSuggestions,
    questionCodes: questions.map((q) => q.code),
    variableExtraInfo: variable.variableExtraInfo
  };

  const isRelative = isRelativeVariable(responseCategories);

  const variableTypePreviewValues = isRelative
    ? getRelativeVariablePreviewValues(questions, responseOptionValues, categoryIndex)
    : getAbsoluteVariablePreviewValues(responseCategories, categoryIndex);

  return { ...commonPreviewValues, ...variableTypePreviewValues };
};

class VariableFeedbackPreview extends Component {
  constructor(props) {
    super(props);

    this.onVariableCategoryChange = this.onVariableCategoryChange.bind(this);
    this.renderVariableCategorySelector = this.renderVariableCategorySelector.bind(this);
    this.getVariableName = this.getVariableName.bind(this);

    this.state = {
      previewCategoryIndex: 0
    };
  }

  onVariableCategoryChange(event) {
    const previewCategoryIndex = Number(event.target.value);
    this.setState({ previewCategoryIndex });
  }

  getVariableName() {
    const { translate: t, lang, variable } = this.props;
    const noVariableName = t('editor.surveySchemaPartPreview.unnamedDimension');
    return getTranslation(variable.name, lang) || noVariableName;
  }

  renderVariableCategorySelector(variable) {
    const { lang, translate: t } = this.props;
    const { responseCategories, order, questions } = variable;

    const variableName = this.getVariableName();
    const getVariableSelectorValue = (key, text) => ({ key, text });
    const getAbsoluteCategoryName = (name) =>
      (getTranslation(name, lang) || t('editor.surveySchemaPartPreview.unnamedCategory'));

    const categories = isRelativeVariable(responseCategories)
      ? responseCategories.map((c, index) =>
        getVariableSelectorValue(
          index,
          t(relativeCategoryNameTranslationKey(c.relativeCategory.type))
        ))
      : responseCategories.map((c, index) =>
        getVariableSelectorValue(
          index,
          getAbsoluteCategoryName(c.absoluteCategory.description)
        ));

    const VARIABLE_SELECT_ID = 'variableSelect';

    return (
      <div className={styles.variableControlHeader}>
        <div className={styles.variableSelectContainer}>
          <label htmlFor={VARIABLE_SELECT_ID}>
            { t('editor.surveySchemaPartPreview.previewDimensionCategory', { variableName })}
          </label>
          <select
            id={VARIABLE_SELECT_ID}
            key={order}
            className={styles.variableSelect}
            value={this.state.previewCategoryIndex}
            onChange={this.onVariableCategoryChange}
          >
            {
            categories.map((c) => <option key={c.key} value={c.key}>{c.text}</option>)
          }
          </select>
        </div>
        <div className={styles.variableQuestionsContainer}>
          <div className={styles.questionListLabel}>
            <p>{t('surveyParts.questions')}</p>
          </div>
          <ul className={styles.questionList}>
            {
              questions.map((q) => (
                <li key={q.code}>
                  <span className={styles.questionCode}>{q.code}</span>
                  <span>{` – ${getTranslation(q.text, lang)}`}</span>
                </li>
              ))
            }
          </ul>
        </div>
      </div>
    );
  }

  render() {
    const { variable, previewLang, translate: t } = this.props;
    const hasQuestions = (v) => (v.questions && v.questions.length > 0);

    if (!hasQuestions(variable)) {
      const variableName = this.getVariableName();
      return (
        <div className={styles.variableControlHeader}>
          <p className={styles.infoText}>
            {t('editor.surveySchemaPartPreview.noLinkedQuestions', { variableName })}
          </p>
        </div>
      );
    }

    const {
      order, name, description, responseCategories
    } = variable;

    if (isRelativeVariable(responseCategories)) {
      responseCategories.sort(compareRelativeCategoriesByType);
    }

    const previewValues = getVariablePreviewValues(variable, this.state.previewCategoryIndex);
    const previewStats = {
      ...previewValues, name, order, description
    };

    return (
      <div key={order}>
        {this.renderVariableCategorySelector(variable)}
        <div className={styles.previewContainer}>
          <VariableSummary
            previewLang={previewLang}
            stats={previewStats}
            previewTranslate={(str) => getTranslationWithLocale(str, previewLang)}
          />
        </div>
      </div>
    );
  }
}

VariableFeedbackPreview.propTypes = {
  variable: variableEditorType.isRequired,
  previewLang: string.isRequired,
  ...langType
};

export default withLang(VariableFeedbackPreview);
